import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLogin, useLogout, useRegister, useUnregister } from './queries/authQueries';
import { STORAGE_KEYS } from '../utils/constants';
import roleManager from '../utils/roleManager';
import { systemNotificationSseClient } from '../services/systemNotificationSse.js';

/**
 * JWT 토큰 인증을 관리하는 커스텀 훅
 */
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN));
  const [loading, setLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [userFeatures, setUserFeatures] = useState([]);

  // 토큰이 유효한지 확인하는 함수
  const isTokenValid = useCallback((token) => {
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      // 토큰 만료 시간 확인
      return payload.exp > currentTime;
    } catch (error) {
      console.error('토큰 파싱 오류:', error);
      return false;
    }
  }, []);

  /**
   * 로그인 응답에서 역할 정보를 추출하고 피처 권한 계산
   * @param {Object|Array} rolesData - 로그인 응답의 Roles 필드 (Items 배열 또는 직접 배열)
   * @returns {Object} { roles: string[], features: string[] } - 역할 배열과 피처 배열 (합집합)
   */
  const extractRolesAndFeatures = useCallback((rolesData) => {
    
    if (!rolesData) {
      console.warn('⚠️ 역할 정보가 없습니다.');
      return { roles: [], features: [] };
    }

    // Roles가 Items를 포함하는 객체인지 확인
    let rolesArray = [];
    if (rolesData.Items && Array.isArray(rolesData.Items)) {
      // { Total: 1, Items: [...] } 형태
      rolesArray = rolesData.Items;
    } else if (Array.isArray(rolesData)) {
      // 직접 배열 형태
      rolesArray = rolesData;
    } else {
      console.warn('⚠️ 역할 데이터 형식이 올바르지 않습니다.');
      return { roles: [], features: [] };
    }

    if (rolesArray.length === 0) {
      console.warn('⚠️ 역할 목록이 비어있습니다.');
      return { roles: [], features: [] };
    }

    // 모든 역할의 피처를 합집합으로 합치기
    const allFeatures = new Set();
    const roleNames = [];

    rolesArray.forEach((roleItem) => {
      const roleName = roleItem.RoleName;
      if (roleName) {
        roleNames.push(roleName);
        
        if (roleItem.FeatureNames && Array.isArray(roleItem.FeatureNames)) {
          roleItem.FeatureNames.forEach((feature) => {
            allFeatures.add(feature);
          });
        } else {
          console.warn(`⚠️ 역할 "${roleName}"에 피처 정보가 없습니다.`);
        }
      } else {
        console.warn('⚠️ RoleName이 없는 역할 항목 발견:', roleItem);
      }
    });

    const featuresArray = Array.from(allFeatures);
    
    return {
      roles: roleNames,
      features: featuresArray
    };
  }, []);

  // React Query mutations
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const registerMutation = useRegister();
  const unregisterMutation = useUnregister();

  // 로그인 함수
  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const response = await loginMutation.mutateAsync(credentials);
      
      const { AccessToken, RefreshToken, UserSysId, UserId, ExpiredDate, Roles } = response;
      
      // 역할 및 피처 정보 추출 (로그인 응답에서 직접 계산)
      const { roles: roleNames, features } = extractRolesAndFeatures(Roles);
      
      // 사용자 정보 설정
      const userData = {
        UserSysId,
        UserId,
        AccessToken,
        RefreshToken,
        ExpiredDate,
        Roles: roleNames // 역할 이름 배열
      };
      
      setToken(AccessToken);
      setUser(userData);
      setUserRoles(roleNames);
      setUserFeatures(features);
      
      // 역할 정보를 로컬 스토리지에 저장
      localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(roleNames));
      
      // 피처 정보를 로컬 스토리지에 저장
      localStorage.setItem(STORAGE_KEYS.USER_FEATURES, JSON.stringify(features));
      
      // 사용자 기본 정보 저장
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
      
      // 시스템 알림 SSE 연결 시작
      systemNotificationSseClient.connect(UserSysId, AccessToken);
      
      return { success: true, user: userData, roles: roleNames, features };
    } catch (error) {
      console.error('❌ 로그인 실패:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [loginMutation, extractRolesAndFeatures]);

  // 로그아웃 함수
  const logout = useCallback(async () => {
    try {
      // UserSysId 가져오기 (user state에 의존하지 않도록)
      const storedUserDataRaw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      let currentUserSysId = null;
      if (storedUserDataRaw) {
        try {
          const storedUserData = JSON.parse(storedUserDataRaw);
          currentUserSysId = storedUserData?.UserSysId;
        } catch (error) {
          console.error('사용자 데이터 파싱 실패:', error);
        }
      }
      
      // UserSysId가 있으면 로그아웃 API 호출
      if (currentUserSysId) {
        try {
          await logoutMutation.mutateAsync(currentUserSysId);
        } catch (error) {
          console.error('로그아웃 API 호출 실패:', error);
          // API 호출이 실패해도 로컬 정리는 진행
        }
      }
      
      // 로컬 스토리지 정리
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
      localStorage.removeItem(STORAGE_KEYS.USER_FEATURES);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      
      // 상태 초기화
      setToken(null);
      setUser(null);
      setUserRoles([]);
      setUserFeatures([]);
      
      // SSE 연결 종료
      systemNotificationSseClient.disconnect();
    } catch (error) {
      console.error('로그아웃 처리 중 오류:', error);
      // 오류가 발생해도 로컬 정리는 강제로 진행
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
          localStorage.removeItem(STORAGE_KEYS.USER_FEATURES);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      setToken(null);
      setUser(null);
      setUserRoles([]);
      setUserFeatures([]);
      systemNotificationSseClient.disconnect();
    }
  }, [logoutMutation]); // user 의존성 제거

  // 회원가입 함수
  const register = useCallback(async (credentials) => {
    setLoading(true);
    try {
      await registerMutation.mutateAsync(credentials);
      return { success: true };
    } catch (error) {
      console.error('회원가입 실패:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [registerMutation]);

  // 회원탈퇴 함수
  const unregister = useCallback(async (credentials) => {
    setLoading(true);
    try {
      await unregisterMutation.mutateAsync(credentials);
      logout(); // 회원탈퇴 성공 시 로그아웃
      return { success: true };
    } catch (error) {
      console.error('회원탈퇴 실패:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [unregisterMutation, logout]);

  // 토큰 갱신 함수
  const refreshToken = useCallback(async () => {
    try {
      return { success: false, error: '토큰 갱신 기능이 구현되지 않았습니다.' };
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      logout();
      return { success: false, error: error.message };
    }
  }, [logout]);

  // 사용자 정보 가져오기 (토큰으로 API 호출)
  const fetchUser = useCallback(async () => {
    const currentToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!currentToken) return;
    
    try {
      const storedRoles = localStorage.getItem(STORAGE_KEYS.USER_ROLES);
      const storedFeatures = localStorage.getItem(STORAGE_KEYS.USER_FEATURES);
      const storedUserDataRaw = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      let storedUserData = null;
      if (storedUserDataRaw) {
        try {
          storedUserData = JSON.parse(storedUserDataRaw);
        } catch (error) {
          console.error('USER_DATA 파싱 실패:', error);
        }
      }
      
      if (storedRoles) {
        try {
          const roles = JSON.parse(storedRoles);
          setUserRoles(roles);
        } catch (error) {
          console.error('역할 정보 파싱 실패:', error);
        }
      }
      
      if (storedFeatures) {
        try {
          const features = JSON.parse(storedFeatures);
          setUserFeatures(features);
        } catch (error) {
          console.error('피처 정보 파싱 실패:', error);
        }
      }
      
      // 현재는 토큰에서 기본 정보만 추출
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      
      if (currentToken) {
        // 토큰 유효성 먼저 확인
        if (!isTokenValid(currentToken)) {
          console.warn('토큰이 만료되었습니다.');
          setToken(null);
          setUser(null);
          setUserRoles([]);
          setUserFeatures([]);
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_ROLES);
          localStorage.removeItem(STORAGE_KEYS.USER_FEATURES);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          return;
        }

        let userDataFromToken = null;
        try {
          const payload = JSON.parse(atob(currentToken.split('.')[1]));
          userDataFromToken = {
            UserId: payload.email || storedUserData?.UserId || 'Unknown',
            UserSysId: storedUserData?.UserSysId,
            AccessToken: currentToken,
            RefreshToken: refreshToken,
            ExpiredDate: new Date(payload.exp * 1000).toISOString(),
            Roles: storedRoles ? JSON.parse(storedRoles) : storedUserData?.Roles || []
          };
        } catch (error) {
          console.error('토큰 파싱 실패:', error);
          // 토큰 파싱 실패 시에도 토큰을 지우지 않음
          // 토큰이 유효하다면 (위에서 확인함) 파싱 실패는 일시적 문제일 수 있음
          // 대신 저장된 사용자 데이터로 복원 시도
          if (storedUserData) {
            userDataFromToken = {
              ...storedUserData,
              AccessToken: currentToken,
              RefreshToken: refreshToken,
            };
          } else {
            // 저장된 데이터도 없으면 기본값 사용
            userDataFromToken = {
              UserId: 'Unknown',
              UserSysId: null,
              AccessToken: currentToken,
              RefreshToken: refreshToken,
              ExpiredDate: null,
              Roles: []
            };
          }
        }
        const finalUserData = {
          ...userDataFromToken,
          ...storedUserData,
          AccessToken: currentToken,
          RefreshToken: refreshToken,
        };
        setUser(finalUserData);
        if (finalUserData.UserSysId) {
          systemNotificationSseClient.connect(finalUserData.UserSysId, currentToken);
        }
      }
    } catch (error) {
      console.error('사용자 정보 조회 실패:', error);
      // fetchUser는 로컬 스토리지에서만 데이터를 읽으므로
      // 여기서 에러가 발생하면 로컬 데이터 파싱 에러일 가능성이 높음
      // 토큰 파싱 에러는 이미 위에서 처리되었으므로
      // 여기서는 경고만 하고 로그아웃하지 않음
      // (실제 인증 실패는 ProtectedRoute에서 토큰 검증 시 감지됨)
      console.warn('사용자 정보 복원 중 에러 발생, 토큰이 유효하면 계속 진행');
    }
  }, []); // 의존성 제거하여 무한 루프 방지

  // 컴포넌트 마운트 시 토큰 유효성 확인
  useEffect(() => {
    const currentToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (currentToken && !isTokenValid(currentToken)) {
      logout();
    } else if (currentToken && token !== currentToken) {
      // 토큰이 변경되었을 때만 상태 업데이트
      setToken(currentToken);
      // 저장된 사용자 정보 복원
      fetchUser();
    } else if (currentToken && !user) {
      // 토큰은 있지만 user가 없을 때만 fetchUser 호출
      fetchUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]); // logout, fetchUser는 안정적인 함수이므로 의존성에서 제외

  return {
    user,
    token,
    loading,
    isAuthenticated: !!token && isTokenValid(token),
    login,
    logout,
    register,
    unregister,
    refreshToken,
    fetchUser,
    userRoles,
    userFeatures,
  };
};

export default useAuth;
